import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "../clients";

const fetchDropDownList = async ({ query = {} }) => {
  return client("/field_dropdown", {
    method: "POST",
  }).then((data: any) => {
    if (typeof (query as any).simulateStatus !== "undefined") {
      const status = Number((query as any).simulateStatus);
      const err: any = new Error("Simulated error");
      err.response = { status, data: { message: "Simulated error" } };
      throw err;
    }
    if (data?.isAxiosError || (data?.status && data.status >= 400)) {
      throw data;
    }
    return data;
  });
};

/**
 * Retrieves taxonomy/field dropdown data for segment builder.
 *
 * @param query - Request parameters
 * @param query.simulateStatus - Optional HTTP status code to simulate an error (testing only)
 * @param options - React Query options (e.g., enabled, select, onError, staleTime, gcTime)
 *
 * @returns React Query result containing dropdown data
 *
 * @remarks
 * - Cache key: ["dropdown-list", query]
 * - Supports error simulation via `query.simulateStatus`
 *
 * @example
 * const { data, isPending, error } = useDropDownList({
 *   query: { simulateStatus: 500 },
 *   options: { enabled: true }
 * });
 */
const useDropDownList = ({ query = {}, options }: any = {}) => {
  //query.simulateStatus = 500;
  return useQuery({
    queryKey: ["dropdown-list", query],
    queryFn: () => fetchDropDownList({ query }),
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 10,
    ...options,
  }) as any;
};

const fetchFolderDropdown = async ({ query = {} }) => {
  return client("/folder_dropdown", {
    method: "POST",
    data: query,
  }).then((data: any) => {
    if (typeof (query as any).simulateStatus !== "undefined") {
      const status = Number((query as any).simulateStatus);
      const err: any = new Error("Simulated error");
      err.response = { status, data: { message: "Simulated error" } };
      throw err;
    }
    if (data?.isAxiosError || (data?.status && data.status >= 400)) {
      throw data;
    }
    return data;
  });
};

/**
 * Retrieves folder dropdown list for segment builder.
 *
 * @param query - Request parameters
 * @param query.country - Optional country filter
 * @param query.brand - Optional brand filter
 * @param query.simulateStatus - Optional HTTP status code to simulate an error (testing only)
 * @param options - React Query options (e.g., enabled, select, onError)
 *
 * @returns React Query result containing folder dropdown data
 *
 * @remarks
 * - Cache key: ["folder-dropdown", query]
 * - Supports error simulation via `query.simulateStatus`
 *
 * @example
 * const { data, isPending, error } = useFolderDropdown({
 *   query: { country: "PL", brand: "BrandX", simulateStatus: 404 },
 *   options: { enabled: true }
 * });
 */
const useFolderDropdown = ({ query = {}, options }: any = {}) => {
  //query.simulateStatus = 500;
  return useQuery({
    queryKey: ["folder-dropdown", query],
    queryFn: () => fetchFolderDropdown({ query }),
    ...options,
  }) as any;
};

const fetchSegmentCampaignsList = async ({ query = {} }) => {
  return client("/segment_campaigns", {
    method: "POST",
    data: query,
  }).then((data: any) => {
    if (typeof (query as any).simulateStatus !== "undefined") {
      const status = Number((query as any).simulateStatus);
      const err: any = new Error("Simulated error");
      err.response = { status, data: { message: "Simulated error" } };
      throw err;
    }
    if (data?.isAxiosError || (data?.status && data.status >= 400)) {
      throw data;
    }
    return data;
  });
};

/**
 * Retrieves campaigns linked to a given segment.
 *
 * @param query - Request parameters
 * @param query.segmentId - Optional segment identifier
 * @param query.simulateStatus - Optional HTTP status code to simulate an error (testing only)
 * @param options - React Query options
 *
 * @returns React Query result containing segment campaigns list
 *
 * @remarks
 * - Cache key: ["segment-campaigns", query]
 * - Supports error simulation via `query.simulateStatus`
 *
 * @example
 * const { data, isPending, error } = useSegmentCampaignsList({
 *   query: { segmentId: "seg-123", simulateStatus: 500 },
 *   options: { enabled: true }
 * });
 */
const useSegmentCampaignsList = ({ query = {}, options }: any = {}) => {
  //query.simulateStatus = 500;
  return useQuery({
    queryKey: ["segment-campaigns", query],
    queryFn: () => fetchSegmentCampaignsList({ query }),
    ...options,
  }) as any;
};

/**
 * Creates or updates a segment (audience).
 *
 * @param options - React Query mutation options (onSuccess, onError, etc.)
 *
 * @returns React Query mutation for segment creation
 *
 * @example
 * const { mutate, isPending } = useCreateSegmentList({ options: { onSuccess: () => {} } });
 * mutate({ country: "PL", segmentName: "My Segment", sections: [], exclusions: [] });
 */
function useCreateSegmentList({ options }: any) {
  return useMutation({
    mutationFn: (reqBody: any) =>
      client("/audience", {
        method: "POST",
        data: reqBody,
      }),
    ...options,
  }) as any;
}

const fetchGetSegmentDetail = async ({ query = {} }) => {
  const res = await client("/fetch_segment", {
    method: "POST",
    data: {
      ...query,
    },
  }).then((data: any) => data);
  if (typeof (query as any).simulateStatus !== "undefined") {
    const status = Number((query as any).simulateStatus);
    const err: any = new Error("Simulated error");
    err.response = { status, data: { message: "Simulated error" } };
    throw err;
  }
  if (res?.isAxiosError || (res?.status && res.status >= 400)) {
    throw res;
  }
  return res;
};

/**
 * Retrieves segment details using /fetch_segment endpoint.
 *
 * @param query - Request parameters
 * @param query.segmentId - Optional segment identifier
 * @param query.tags - Optional tag filter
 * @param query.simulateStatus - Optional HTTP status code to simulate an error (testing only)
 * @param options - React Query options
 *
 * @returns React Query result containing segment details
 *
 * @remarks
 * - Cache key: ["segment-detail", query]
 * - Supports error simulation via `query.simulateStatus`
 *
 * @example
 * const { data, isPending, error } = useGetSegmentDetail({
 *   query: { segmentId: "seg-123", simulateStatus: 500 },
 *   options: { enabled: true }
 * });
 */
const useGetSegmentDetail = ({ query = {}, options }: any = {}) => {
  //query.simulateStatus = 500;
  return useQuery({
    queryKey: ["segment-detail", query],
    queryFn: () => fetchGetSegmentDetail({ query }),
    ...options,
  }) as any;
};

const fetchGetValueDropdown = async ({ query = {} }) => {
  return client("/value_dropdown", {
    method: "POST",
    data: {
      ...query,
    },
  }).then((data: any) => data);
};

/**
 * Retrieves value dropdown for selected field/category.
 *
 * @param query - Request parameters
 * @param query.field - Optional field name
 * @param query.category - Optional category name
 * @param options - React Query options
 *
 * @returns React Query result containing value dropdown
 *
 * @remarks
 * - Cache key: ["value-dropdown", query]
 *
 * @example
 * const { data } = useGetValueDropdown({ query: { field: "age" }, options: { enabled: true } });
 */
const useGetValueDropdown = ({ query = {}, options }: any = {}) => {
  return useQuery({
    queryKey: ["value-dropdown", query],
    queryFn: () => fetchGetValueDropdown({ query }),
    ...options,
  }) as any;
};

const fetchGetFolderAssign = async ({ query = {} }) => {
  return client("/folder_assign", {
    method: "POST",
    data: {
      ...query,
    },
  }).then((data: any) => {
    if (typeof (query as any).simulateStatus !== "undefined") {
      const status = Number((query as any).simulateStatus);
      const err: any = new Error("Simulated error");
      err.response = { status, data: { message: "Simulated error" } };
      throw err;
    }
    if (data?.isAxiosError || (data?.status && data.status >= 400)) {
      throw data;
    }
    return data;
  });
};

/**
 * Retrieves folder assignment information.
 *
 * @param query - Request parameters
 * @param query.country - Optional country filter
 * @param query.brand - Optional brand filter
 * @param query.simulateStatus - Optional HTTP status code to simulate an error (testing only)
 * @param options - React Query options
 *
 * @returns React Query result containing folder assignment
 *
 * @remarks
 * - Cache key: ["folder-assign", query]
 * - Supports error simulation via `query.simulateStatus`
 *
 * @example
 * const { data } = useGetFolderAssign({ query: { country: "PL", simulateStatus: 404 } });
 */
const useGetFolderAssign = ({ query = {}, options }: any = {}) => {
  //query.simulateStatus = 500;
  return useQuery({
    queryKey: ["folder-assign", query],
    queryFn: () => fetchGetFolderAssign({ query }),
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 10,
    ...options,
  }) as any;
};

/**
 * Updates segment end date (delete/close segment workflow).
 *
 * @param options - React Query mutation options
 * @param user - User identifier performing the update
 * @param segmentName - Segment name
 * @param segmentId - Segment identifier
 *
 * @returns React Query mutation to update segment end date
 *
 * @example
 * const { mutate } = useUpdateSegmentEndDate({ options: { onSuccess: () => {} }, user, segmentName, segmentId });
 * mutate({ endDate: "2025-12-31" });
 */
function useUpdateSegmentEndDate({ options, user, segmentName, segmentId }: any) {
  return useMutation({
    mutationFn: async (reqBody: any) =>{
      const payload = { user, segmentName, segmentId, ...reqBody };
      try {
        const res = await client("/delete_segment", { method: "POST", data: payload });
        return res;
      } catch (err) {
        throw err;
      }
    },
    ...options,
  }) as any;
}

export {
  useCreateSegmentList,
  useDropDownList,
  useGetFolderAssign,
  useSegmentCampaignsList,
  useGetSegmentDetail,
  useGetValueDropdown,
  useFolderDropdown,
  useUpdateSegmentEndDate,
};
